import { useCallback, useEffect, useState } from "react";
import { idToken, signIn } from "./auth";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const tok = await idToken();
  if (!tok) {
    await signIn();
    throw new ApiError(401, "Signing in…");
  }
  const res = await fetch(`/api${path}`, {
    method: opts.method ?? (opts.body ? "POST" : "GET"),
    headers: { authorization: `Bearer ${tok}`, ...(opts.body ? { "content-type": "application/json" } : {}) },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    await signIn();
    throw new ApiError(401, "Signing in…");
  }
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`);
  return data as T;
}

/** Load data for a page; `reload()` refetches. */
export function useApi<T = any>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      setData(await api<T>(path));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => {
    void load();
  }, [load]);
  return { data, error, loading, reload: load, setData };
}

export const APP_NAMES = { ja: "Job Automation", expense: "Expense Management", time: "Time Management", payables: "Payables Management", radar: "RADAR" } as const;
export type AppId = keyof typeof APP_NAMES;
export const APP_ROLES: Record<AppId, string[]> = { ja: ["designer", "run", "status", "history", "scheduler"], expense: ["user"], time: ["user"], payables: ["user"], radar: ["export", "reports", "sql"] };
export const ROLE_LABELS: Record<string, string> = { designer: "Designer", run: "Run", status: "Status", history: "History", scheduler: "Scheduler", user: "Access", export: "Compass export", reports: "Reports", sql: "SQL library" };
export const roleList = (roles: string[]) => roles.map((r) => ROLE_LABELS[r] ?? r).join(", ");

export interface Me {
  kind: "platform" | "tenant";
  email: string;
  name?: string;
  tenantAdmin?: boolean;
  tenant?: { tenantId: string; name: string; signInMode: string; apps: AppId[] };
  apps?: { id: AppId; name: string; roles: string[] }[];
}
